import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // This is critical for sending cookies with requests
});

// Create a separate client for public endpoints that doesn't send cookies
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Don't send cookies for public endpoints
});

// Add auth token interceptor for localStorage token (Google OAuth users)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login/public pages
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/register', '/events', '/event/', '/invite/', '/verify/', '/verify-ticket/', '/invitation-', '/code-of-conduct'];
      const isPublicPath = publicPaths.some(path => currentPath.startsWith(path));
      
      if (!isPublicPath) {
        // Session expired or invalid - clear local data and redirect to login
        localStorage.removeItem('organizer');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
// Who a broadcast email goes to: confirmed attendees, or people still holding an invitation
export type EmailAudience = 'attendees' | 'invitees';

export interface Event {
  id: number;
  title: string;
  description?: string;
  date_time: string;
  end_time?: string;
  timezone: string;
  venue: string;
  max_attendees: number;
  reserved_tickets: number;
  admission_policy: 'review' | 'auto';
  waitlist_when_full: boolean;
  show_attendee_list: boolean;
  approved_count: number;
  pending_count: number;
  waitlist_count: number;
  rejected_count: number;
  available_spots: number;
  capacity_percentage: number;
  invitation_sent_count: number;
  invitation_opened_count: number;
  invitation_registered_count: number;
  invitation_declined_count: number;
  client_count: number;
  prospect_count: number;
  partner_count: number;
  staff_count: number;
  dietary_requirements_count: number;
  status: 'draft' | 'active' | 'archived' | 'cancelled';
  event_type: 'social' | 'meetup' | 'conference' | 'casual';
  archived_at?: string;
  checked_in_count: number;
  total_registrations: number;
  google_maps_link?: string;
  entry_requirement?: string;
  badge_subtitle?: string;
  badge_title?: string;
}

export interface PublicEvent {
  id: number;
  title: string;
  description?: string;
  date_time: string;
  end_time?: string;
  timezone: string;
  venue: string;
  available_spots: number;
  max_attendees: number;
  admission_policy: 'review' | 'auto';
  registration_state: 'review' | 'open' | 'waitlist' | 'closed';
  at_capacity: boolean;
  show_attendee_list: boolean;
  status?: 'draft' | 'active' | 'archived' | 'cancelled';
  event_type?: 'social' | 'meetup' | 'conference' | 'casual';
  google_maps_link?: string;
  entry_requirement?: string;
  sponsors?: PublicSponsor[];
}

// Sponsors
export type SponsorTier = 'headline' | 'partner' | 'supporter';

export interface Sponsor {
  id: number;
  name: string;
  website_url?: string | null;
  has_logo: boolean;
  logo_content_type?: string | null;
  logo_url?: string | null;
  is_active: boolean;
  event_count: number;
  created_at: string;
  updated_at: string;
}

export interface SponsorCreate {
  name: string;
  website_url?: string | null;
  logo_data?: string;
  logo_content_type?: string;
  logo_filename?: string;
  is_active?: boolean;
}

export interface SponsorUpdate extends Partial<SponsorCreate> {
  remove_logo?: boolean;
}

export interface EventSponsor {
  id: number;
  sponsor_id: number;
  name: string;
  website_url?: string | null;
  logo_url?: string | null;
  tier: SponsorTier;
  sort_order: number;
  show_on_event_page: boolean;
  show_on_badge: boolean;
  show_in_emails: boolean;
  show_on_cards: boolean;
}

export interface EventSponsorAttach {
  sponsor_id: number;
  tier?: SponsorTier;
  show_on_event_page?: boolean;
  show_on_badge?: boolean;
  show_in_emails?: boolean;
  show_on_cards?: boolean;
}

export interface EventSponsorUpdate {
  tier?: SponsorTier;
  sort_order?: number;
  show_on_event_page?: boolean;
  show_on_badge?: boolean;
  show_in_emails?: boolean;
  show_on_cards?: boolean;
}

export interface PublicSponsor {
  name: string;
  website_url?: string | null;
  logo_url: string;
  tier: SponsorTier;
}

/** Absolute URL for a sponsor logo path such as "/api/sponsors/3/logo". */
export const sponsorLogoSrc = (logoUrl?: string | null): string | undefined => {
  if (!logoUrl) return undefined;
  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${logoUrl}`;
};

export interface TicketRequest {
  given_name: string;
  family_name: string;
  pronouns?: string;
  email: string;
  company: string;
  role: string;
  relationship_type: string;
  dietary_requirements: string;
  non_dietary_requirements: string;
  how_heard: string;
  code_of_conduct_accepted: boolean;
  privacy_policy_accepted: boolean;
  entry_requirement_accepted?: boolean;
  newsletter_opt_in: boolean;
  recaptcha_token?: string;
}

export interface TicketResponse {
  id: number;
  event_id: number;
  name: string;
  given_name?: string;
  family_name?: string;
  pronouns?: string;
  email: string;
  company: string;
  phone?: string;
  linkedin_url?: string;
  role?: string;
  relationship_type?: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  approved_at?: string;
  notes?: string;
  checked_in: boolean;
  checked_in_at?: string;
  code_of_conduct_accepted: boolean;
  email_verified: boolean;
  verification_sent_at?: string;
  newsletter_opt_in: boolean;
  ticket_code?: string;
}

export interface InvitationCreate {
  email: string;
  name: string;
  company: string;
  relationship_type_id?: number;
  custom_message?: string;
}

export interface BulkInvitationCreate {
  invitations: InvitationCreate[];
  custom_message?: string;
  custom_subject?: string;
}

export interface InvitationResponse {
  id: number;
  event_id: number;
  email: string;
  name: string;
  company: string;
  invitation_code: string;
  status: 'sent' | 'opened' | 'registered' | 'declined';
  sent_at: string;
  invitation_link: string;
  has_registered: boolean;
}

export interface InvitationPublicResponse {
  event_title: string;
  event_description?: string;
  event_date_time: string;
  event_venue: string;
  invitee_name: string;
  invitee_email: string;
  invitee_company: string;
  has_registered: boolean;
}

export interface InvitationAcceptData {
  given_name: string;
  family_name: string;
  pronouns?: string;
  email: string;
  company: string;
  role: string;
  relationship_type: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  newsletter_opt_in: boolean;
  entry_requirement_accepted?: boolean;
}

export interface EmailTemplateCreate {
  name: string;
  subject: string;
  body: string;
  category?: string;
  is_active?: boolean;
}

export interface Timezone {
  value: string;
  label: string;
  display_name: string;
  offset: string;
}

export interface EmailTemplateUpdate {
  name?: string;
  subject?: string;
  body?: string;
  category?: string;
  is_active?: boolean;
}

export interface EmailTemplateResponse {
  id: number;
  name: string;
  subject: string;
  body: string;
  category?: string;
  is_active: boolean;
  is_default: boolean;
  organization_id?: number;
  created_at: string;
  updated_at: string;
  variables_used?: string[];
}

export interface SettingsUpdate {
  company_name?: string;
  company_email?: string;
  logo_data?: string;
  logo_filename?: string;
  logo_content_type?: string;
}

export interface SettingsResponse {
  company_name: string;
  company_email: string;
  logo_filename?: string;
  logo_data?: string;
  logo_content_type?: string;
  updated_at: string;
}

export interface OrganizerProfile {
  id: number;
  email: string;
  name: string;
  company?: string;
  phone?: string;
  job_title?: string;
  role: 'primary_admin' | 'admin' | 'organizer';
  organization_id: number;
}

export interface OrganizerProfileUpdate {
  name?: string;
  company?: string;
  phone?: string;
  job_title?: string;
  current_password?: string;
  new_password?: string;
}

export interface Organization {
  id: number;
  name: string;
  created_at: string;
}

// Unified Registration Types
export type RegistrationStatus = 
  // Invitation phase
  | 'invited'            // Just sent invitation
  | 'opened'             // Clicked invitation link (renamed from invitation_opened)
  | 'declined'           // Declined invitation
  
  // Registration phase  
  | 'registered'         // Completed registration form (from invitation)
  | 'pending'            // Direct registration awaiting email verification
  | 'waitlist'           // Email verified, awaiting admin approval
  | 'approved'           // Approved ticket
  | 'rejected'           // Rejected application
  | 'cancelled'          // Cancelled ticket
  
  // Event day
  | 'checked_in'         // Checked in at event
  | 'checked_out'        // Left event
  | 'no_show';           // Approved but did not attend

export interface Registration {
  id: number;
  event_id: number;
  status: RegistrationStatus;
  
  // Invitation fields
  invitation_code?: string;
  relationship_type_id?: number;
  custom_message?: string;
  
  // Basic info
  email: string;
  name?: string;
  company?: string;
  
  // Detailed info
  given_name?: string;
  family_name?: string;
  pronouns?: string;
  phone?: string;
  linkedin_url?: string;
  role?: string;
  relationship_type?: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  
  // Ticket info
  ticket_code?: string;
  notes?: string;
  
  // Timestamps
  created_at: string;
  invitation_sent_at?: string;
  opened_at?: string;
  responded_at?: string;
  approved_at?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  reconfirmed_at?: string;
  expires_at?: string;
  
  // Verification and compliance
  email_verified: boolean;
  verification_sent_at?: string;
  code_of_conduct_accepted: boolean;
  privacy_policy_accepted: boolean;
  newsletter_opt_in: boolean;
  
  // Computed properties
  is_invitation_based: boolean;
  has_registered: boolean;
  is_approved: boolean;
  
  // Optional invitation link (for manual invitations)
  invitation_link?: string;
}

export interface RegistrationCreate {
  email: string;
  name?: string;
  company?: string;
  relationship_type_id?: number;
  custom_message?: string;
  
  // For direct registrations
  given_name?: string;
  family_name?: string;
  pronouns?: string;
  phone?: string;
  linkedin_url?: string;
  role?: string;
  relationship_type?: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  code_of_conduct_accepted?: boolean;
  privacy_policy_accepted?: boolean;
  entry_requirement_accepted?: boolean;
  newsletter_opt_in?: boolean;
  recaptcha_token?: string;
}

export interface RegistrationUpdate {
  given_name?: string;
  family_name?: string;
  pronouns?: string;
  email?: string;
  company?: string;
  role?: string;
  relationship_type?: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  newsletter_opt_in?: boolean;
  notes?: string;
  status?: RegistrationStatus;
}

export interface BulkRegistrationCreate {
  registrations: RegistrationCreate[];
  relationship_type_id?: number;
  template_id?: number;  // Use specific template for invitations
  custom_message?: string;
  custom_subject?: string;
  send_email?: boolean;  // Whether to send invitation emails automatically
}

export interface BulkInvitationResult {
  sent: Registration[];
  skipped: Array<{ email: string; reason: string }>;
  total_requested: number;
  total_sent: number;
  total_skipped: number;
}

// Past attendees for invitation purposes
export interface PastAttendee {
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  company?: string;
  role?: string;
  relationship_type?: string;
  events_attended: Array<{
    id: number;
    title: string;
    date: string;
    status: string;
  }>;
  last_attendance_date?: string;
  total_events_attended: number;
}

export interface PastAttendeesResponse {
  attendees: PastAttendee[];
  total_count: number;
  events_included: Array<{
    id: number;
    title: string;
    date: string;
  }>;
}

export interface RegistrationPublicResponse {
  event_title: string;
  event_description?: string;
  event_date_time: string;
  event_venue: string;
  event_google_maps_link?: string;
  event_entry_requirement?: string;
  event_type?: string;
  invitee_name?: string;
  invitee_email: string;
  invitee_company?: string;
  invitee_relationship_type?: string;
  has_registered: boolean;
}

export interface RegistrationAcceptData {
  given_name: string;
  family_name: string;
  pronouns?: string;
  email: string;
  company: string;
  role: string;
  relationship_type: string;
  dietary_requirements?: string;
  non_dietary_requirements?: string;
  how_heard?: string;
  newsletter_opt_in: boolean;
  entry_requirement_accepted?: boolean;
}

export interface OrganizerListItem {
  id: number;
  email: string;
  name: string;
  company?: string;
  phone?: string;
  job_title?: string;
  role: 'primary_admin' | 'admin' | 'organizer';
  is_active: boolean;
  invited_by_id?: number;
  created_at: string;
}

export interface OrganizerInvite {
  email: string;
  name: string;
  role: 'admin' | 'organizer';
}

export interface OrganizerRoleUpdate {
  role: 'admin' | 'organizer';
}

export interface OrganizerUpdate {
  name?: string;
  email?: string;
  company?: string;
  phone?: string;
  job_title?: string;
}

// Auth API
export const authApi = {
  register: async (email: string, password: string, name: string, company: string, phone: string, job_title: string) => {
    const response = await apiClient.post('/auth/register', { email, password, name, company, phone, job_title });
    return response.data;
  },
  
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/token', 
      new URLSearchParams({
        username: email,
        password: password,
      }), 
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response.data;
  },
  
  getProfile: async (): Promise<OrganizerProfile> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  updateProfile: async (updates: OrganizerProfileUpdate): Promise<OrganizerProfile> => {
    const response = await apiClient.put('/auth/me', updates);
    return response.data;
  },
  
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/auth/verify/${token}`);
    return response.data;
  },
  
  resendVerification: async (): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-verification');
    return response.data;
  },
  
  logout: async (): Promise<{ message: string }> => {
    try {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } finally {
      // Always clear localStorage regardless of API response
      localStorage.removeItem('token');
      localStorage.removeItem('organizer');
    }
  },
};

// Events API
export const eventsApi = {
  // Public endpoints
  getPublicEvents: async (): Promise<PublicEvent[]> => {
    const response = await publicApiClient.get('/events/public');
    return response.data;
  },
  
  getPublicEvent: async (id: number): Promise<PublicEvent> => {
    const response = await publicApiClient.get(`/events/public/${id}`);
    return response.data;
  },
  
  getPublicAttendees: async (id: number): Promise<{ show_list: boolean; total: number; attendees: Array<{ attendee_type: string; job_title: string }> }> => {
    const response = await publicApiClient.get(`/events/public/${id}/attendees`);
    return response.data;
  },
  
  // Organizer endpoints
  getOrganizerEvents: async (): Promise<Event[]> => {
    const response = await apiClient.get('/events/');
    return response.data;
  },
  
  getEvent: async (id: number): Promise<Event> => {
    const response = await apiClient.get(`/events/${id}`);
    return response.data;
  },
  
  createEvent: async (event: {
    title: string;
    description?: string;
    date_time: string;
    timezone?: string;
    venue: string;
    max_attendees: number;
    reserved_tickets?: number;
    admission_policy?: 'review' | 'auto';
    waitlist_when_full?: boolean;
    event_type?: string;
    google_maps_link?: string;
    entry_requirement?: string;
    badge_subtitle?: string;
    badge_title?: string;
  }) => {
    const response = await apiClient.post('/events/', event);
    return response.data;
  },
  
  updateEvent: async (id: number, event: Partial<Event>) => {
    const response = await apiClient.put(`/events/${id}`, event);
    return response.data;
  },
  
  archiveEvent: async (id: number) => {
    const response = await apiClient.post(`/events/${id}/archive`);
    return response.data;
  },
  
  toggleEventStatus: async (id: number): Promise<{ message: string; status: string }> => {
    const response = await apiClient.put(`/events/${id}/toggle-status`);
    return response.data;
  },
  
  restoreEvent: async (id: number) => {
    const response = await apiClient.post(`/events/${id}/restore`);
    return response.data;
  },
  
  deleteEvent: async (id: number) => {
    const response = await apiClient.delete(`/events/${id}?force=true`);
    return response.data;
  },
  
  sendEmailBroadcast: async (
    eventId: number, 
    subject: string, 
    message: string, 
    includeTicketInfo: boolean = true,
    recipientIds?: number[],
    audience: EmailAudience = 'attendees',
    includeAttendanceButtons: boolean = false
  ) => {
    const response = await apiClient.post(`/events/${eventId}/email-broadcast`, {
      subject,
      message,
      include_ticket_info: includeTicketInfo,
      recipient_ids: recipientIds,
      audience,
      include_attendance_buttons: includeAttendanceButtons
    });
    return response.data;
  },
  
  getEventTemplate: async (eventId: number): Promise<{ invitation_template: string | null; invitation_subject: string | null }> => {
    const response = await apiClient.get(`/events/${eventId}/template`);
    return response.data;
  },
  
  exportNameBadges: async (eventId: number, approvedOnly: boolean = true, totalBadges?: number): Promise<Blob> => {
    const response = await apiClient.get(`/events/${eventId}/badges`, {
      params: { 
        approved_only: approvedOnly,
        total_badges: totalBadges 
      },
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportNameBadgesNoQR: async (eventId: number, approvedOnly: boolean = true, totalBadges?: number): Promise<Blob> => {
    const response = await apiClient.get(`/events/${eventId}/badges-no-qr`, {
      params: { 
        approved_only: approvedOnly,
        total_badges: totalBadges 
      },
      responseType: 'blob'
    });
    return response.data;
  },
  
  getTimezones: async (): Promise<Timezone[]> => {
    const response = await apiClient.get('/events/timezones');
    return response.data;
  },
};

// Legacy APIs removed - use registrationsApi instead

export const settingsApi = {
  getSettings: async (): Promise<SettingsResponse> => {
    const response = await apiClient.get('/settings/');
    return response.data;
  },
  
  updateSettings: async (data: SettingsUpdate): Promise<SettingsResponse> => {
    const response = await apiClient.put('/settings/', data);
    return response.data;
  },
  
  getPublicSettings: async (): Promise<SettingsResponse> => {
    const response = await publicApiClient.get('/settings/public');
    return response.data;
  },
};

// Sponsors API (organisation-wide library)
export const sponsorsApi = {
  listSponsors: async (): Promise<Sponsor[]> => {
    const response = await apiClient.get('/sponsors/');
    return response.data;
  },

  getSponsor: async (sponsorId: number): Promise<Sponsor> => {
    const response = await apiClient.get(`/sponsors/${sponsorId}`);
    return response.data;
  },

  createSponsor: async (data: SponsorCreate): Promise<Sponsor> => {
    const response = await apiClient.post('/sponsors/', data);
    return response.data;
  },

  updateSponsor: async (sponsorId: number, data: SponsorUpdate): Promise<Sponsor> => {
    const response = await apiClient.put(`/sponsors/${sponsorId}`, data);
    return response.data;
  },

  deleteSponsor: async (sponsorId: number): Promise<void> => {
    await apiClient.delete(`/sponsors/${sponsorId}`);
  },
};

// Event Sponsors API (sponsors attached to a specific event)
export const eventSponsorsApi = {
  list: async (eventId: number): Promise<EventSponsor[]> => {
    const response = await apiClient.get(`/events/${eventId}/sponsors`);
    return response.data;
  },

  attach: async (eventId: number, data: EventSponsorAttach): Promise<EventSponsor> => {
    const response = await apiClient.post(`/events/${eventId}/sponsors`, data);
    return response.data;
  },

  reorder: async (eventId: number, sponsorIds: number[]): Promise<void> => {
    await apiClient.put(`/events/${eventId}/sponsors/reorder`, { sponsor_ids: sponsorIds });
  },

  update: async (eventId: number, sponsorId: number, data: EventSponsorUpdate): Promise<EventSponsor> => {
    const response = await apiClient.put(`/events/${eventId}/sponsors/${sponsorId}`, data);
    return response.data;
  },

  detach: async (eventId: number, sponsorId: number): Promise<void> => {
    await apiClient.delete(`/events/${eventId}/sponsors/${sponsorId}`);
  },
};

// Organizer Management API
export const organizersApi = {
  getOrganizers: async (): Promise<OrganizerListItem[]> => {
    const response = await apiClient.get('/organizers/');
    return response.data;
  },
  
  getOrganization: async (): Promise<Organization> => {
    const response = await apiClient.get('/organizers/organization');
    return response.data;
  },
  
  inviteOrganizer: async (data: OrganizerInvite): Promise<{ message: string; organizer_id: number }> => {
    const response = await apiClient.post('/organizers/invite', data);
    return response.data;
  },
  
  updateOrganizerRole: async (organizerId: number, data: OrganizerRoleUpdate): Promise<OrganizerListItem> => {
    const response = await apiClient.put(`/organizers/${organizerId}/role`, data);
    return response.data;
  },
  
  updateOrganizer: async (organizerId: number, data: OrganizerUpdate): Promise<OrganizerListItem> => {
    const response = await apiClient.put(`/organizers/${organizerId}`, data);
    return response.data;
  },
  
  deactivateOrganizer: async (organizerId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/organizers/${organizerId}`);
    return response.data;
  },
};

// Relationship Types API
export interface RelationshipType {
  id: number;
  organization_id: number;
  name: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RelationshipTypeCreate {
  name: string;
  display_name: string;
}

export interface RelationshipTypeUpdate {
  display_name?: string;
  is_active?: boolean;
}

// Email Templates API
export const emailTemplatesApi = {
  // List all templates
  listTemplates: async (category?: string): Promise<EmailTemplateResponse[]> => {
    const params = category ? { category } : {};
    const response = await apiClient.get('/email-templates', { params });
    return response.data;
  },

  // Get a specific template
  getTemplate: async (templateId: number): Promise<EmailTemplateResponse> => {
    const response = await apiClient.get(`/email-templates/${templateId}`);
    return response.data;
  },

  // Create a new template
  createTemplate: async (data: EmailTemplateCreate): Promise<EmailTemplateResponse> => {
    const response = await apiClient.post('/email-templates', data);
    return response.data;
  },

  // Update a template
  updateTemplate: async (templateId: number, data: EmailTemplateUpdate): Promise<EmailTemplateResponse> => {
    const response = await apiClient.put(`/email-templates/${templateId}`, data);
    return response.data;
  },

  // Delete a template
  deleteTemplate: async (templateId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/email-templates/${templateId}`);
    return response.data;
  },

  // Duplicate a template
  duplicateTemplate: async (templateId: number): Promise<EmailTemplateResponse> => {
    const response = await apiClient.post(`/email-templates/${templateId}/duplicate`);
    return response.data;
  },
};

export const relationshipTypesApi = {
  getRelationshipTypes: async (): Promise<RelationshipType[]> => {
    const response = await apiClient.get('/relationship-types/');
    return response.data;
  },
  
  createRelationshipType: async (data: RelationshipTypeCreate): Promise<RelationshipType> => {
    const response = await apiClient.post('/relationship-types/', data);
    return response.data;
  },
  
  updateRelationshipType: async (typeId: number, data: RelationshipTypeUpdate): Promise<RelationshipType> => {
    const response = await apiClient.put(`/relationship-types/${typeId}`, data);
    return response.data;
  },
  
  deleteRelationshipType: async (typeId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/relationship-types/${typeId}`);
    return response.data;
  },
};

// Unified Registrations API
export const registrationsApi = {
  // Send invitations (bulk)
  sendInvitations: async (eventId: number, data: BulkRegistrationCreate): Promise<BulkInvitationResult> => {
    const response = await apiClient.post(`/registrations/invitations/${eventId}/send`, data);
    return response.data;
  },

  // Get past attendees for invitation purposes
  getPastAttendees: async (
    eventId: number,
    excludeCurrentEvent: boolean = true,
    eventIds?: string,
    relationshipTypes?: string
  ): Promise<PastAttendeesResponse> => {
    const params: any = {
      exclude_current_event: excludeCurrentEvent
    };
    if (eventIds) params.event_ids = eventIds;
    if (relationshipTypes) params.relationship_types = relationshipTypes;

    const response = await apiClient.get(`/registrations/invitations/${eventId}/past-attendees`, { params });
    return response.data;
  },
  
  // Get all registrations for an event
  getEventRegistrations: async (eventId: number, status?: RegistrationStatus, invitationOnly?: boolean): Promise<Registration[]> => {
    const params: any = {};
    if (status) params.status = status;
    if (invitationOnly !== undefined) params.invitation_only = invitationOnly;
    const response = await apiClient.get(`/registrations/${eventId}/registrations`, { params });
    return response.data;
  },
  
  // Direct registration (public endpoint)
  requestRegistration: async (eventId: number, request: RegistrationCreate): Promise<Registration> => {
    const response = await publicApiClient.post(`/registrations/request/${eventId}`, request);
    return response.data;
  },
  
  // Update registration
  updateRegistration: async (registrationId: number, updates: RegistrationUpdate): Promise<Registration> => {
    const response = await apiClient.put(`/registrations/${registrationId}`, updates);
    return response.data;
  },
  
  // Update registration status only
  updateRegistrationStatus: async (registrationId: number, status: RegistrationStatus, notes?: string, overrideCapacity?: boolean): Promise<Registration> => {
    const response = await apiClient.put(`/registrations/${registrationId}/status`, { status, notes, override_capacity: overrideCapacity || false });
    return response.data;
  },

  // Bulk status update
  bulkUpdateStatus: async (registrationIds: number[], status: RegistrationStatus, notes?: string, overrideCapacity?: boolean) => {
    const response = await apiClient.post('/registrations/bulk-status-update', {
      registration_ids: registrationIds,
      status,
      notes,
      override_capacity: overrideCapacity || false
    });
    return response.data;
  },
  
  // Check in/out
  checkInRegistration: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/checkin/${registrationId}/check-in`);
    return response.data;
  },
  
  checkOutRegistration: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/checkin/${registrationId}/check-out`);
    return response.data;
  },

  markNoShow: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/checkin/${registrationId}/no-show`);
    return response.data;
  },

  undoNoShow: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/checkin/${registrationId}/undo-no-show`);
    return response.data;
  },
  
  // Delete registration
  deleteRegistration: async (registrationId: number) => {
    const response = await apiClient.delete(`/registrations/${registrationId}`);
    return response.data;
  },
  
  // Manually approve an invitation
  manuallyApproveInvitation: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/${registrationId}/manual-approve`);
    return response.data;
  },

  // Resend verification email
  resendVerificationEmail: async (registrationId: number) => {
    const response = await apiClient.post(`/registrations/emails/${registrationId}/resend-verification`);
    return response.data;
  },
  
  resendTicketEmail: async (registrationId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/registrations/emails/${registrationId}/resend-ticket`);
    return response.data;
  },
  
  // Public invitation endpoints
  getInvitationDetails: async (invitationCode: string): Promise<RegistrationPublicResponse> => {
    const response = await publicApiClient.get(`/registrations/invitations/${invitationCode}/details`);
    return response.data;
  },
  
  acceptInvitation: async (invitationCode: string, data: RegistrationAcceptData) => {
    const response = await publicApiClient.post(`/registrations/invitations/${invitationCode}/accept`, data);
    return response.data;
  },
  
  declineInvitation: async (invitationCode: string) => {
    const response = await publicApiClient.post(`/registrations/invitations/${invitationCode}/decline`);
    return response.data;
  },
  
  // Verify registration email
  verifyRegistration: async (token: string): Promise<{
    message: string;
    already_verified: boolean;
    resent?: boolean;
    event_id?: number;
    attendee_name?: string;
  }> => {
    const response = await publicApiClient.get(`/registrations/verify/email/${token}`);
    return response.data;
  },
  
  // Resend invitation
  resendInvitation: async (registrationId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/registrations/emails/${registrationId}/resend-invitation`);
    return response.data;
  },
  
  // Resend verification email
  resendVerification: async (registrationId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/registrations/emails/${registrationId}/resend-verification`);
    return response.data;
  },
  
  // Manually mark registration as verified
  markAsVerified: async (registrationId: number): Promise<Registration> => {
    const response = await apiClient.post(`/registrations/${registrationId}/mark-verified`);
    return response.data;
  },
  
  // Verify ticket (for viewing ticket details)
  verifyTicket: async (ticketCode: string): Promise<{
    event_title: string;
    event_date_time: string;
    event_venue: string;
    attendee_name: string;
    attendee_email: string;
    attendee_company: string;
    ticket_code: string;
    status: RegistrationStatus;
    is_approved: boolean;
    checked_in: boolean;
    checked_in_at?: string;
  }> => {
    const response = await publicApiClient.get(`/registrations/verify/ticket/${ticketCode}`);
    return response.data;
  },
  
  // Scan QR code and check in attendee
  scanQRCode: async (ticketCode: string, deviceInfo?: string): Promise<{
    success: boolean;
    already_checked_in: boolean;
    message: string;
    registration: {
      id: number;
      event_title: string;
      event_date_time: string;
      event_venue: string;
      attendee_name: string;
      attendee_email: string;
      attendee_company: string;
      ticket_code: string;
      status: string;
      checked_in_at: string | null;
      dietary_requirements?: string;
      accessibility_requirements?: string;
    };
  }> => {
    const response = await apiClient.post('/registrations/checkin/scan', {
      ticket_code: ticketCode,
      device_info: deviceInfo
    });
    return response.data;
  },
  
  // Get scan history for an event
  getScanHistory: async (eventId: number, limit: number = 50): Promise<{
    event_id: number;
    event_title: string;
    total_checked_in: number;
    recent_scans: Array<{
      id: number;
      attendee_name: string;
      attendee_email: string;
      company: string;
      checked_in_at: string;
      ticket_code: string;
      ip_address?: string;
      performed_by?: string;
    }>;
  }> => {
    const response = await apiClient.get(`/registrations/checkin/scan-history?event_id=${eventId}&limit=${limit}`);
    return response.data;
  },
  
  // Export registrations as CSV
  exportCSV: async (eventId: number, statuses?: RegistrationStatus[], includeFields?: string[]): Promise<Blob> => {
    const params: any = {};
    if (statuses && statuses.length > 0) {
      params.statuses = statuses.join(',');
    }
    if (includeFields && includeFields.length > 0) {
      params.include_fields = includeFields.join(',');
    }
    
    const response = await apiClient.get(`/registrations/${eventId}/export/csv`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  },
};